import { createFileRoute } from '@tanstack/react-router';
import { DataLoadError } from '@/components/DataLoadError';
import { StatCard } from '@/components/ui/StatCard';
import { StatusBadge } from '@/components/ui/StatusBadge';
import { SCHEDULER_JOBS } from '@/data/mock';
import { useSchedulerJobs } from '@/lib/use-admin-data';
import { cn } from '@/lib/utils';
import { Repeat, PlayCircle, PauseCircle, XCircle, Play } from '@phosphor-icons/react';

function SchedulerPage() {
  const { data: jobs = [], isError: jobsError } = useSchedulerJobs();
  const totalJobs = jobs.length;
  const active = jobs.filter((j) => j.enabled).length;
  const paused = jobs.filter((j) => !j.enabled).length;
  const failedLast24h = jobs.filter((j) => j.lastStatus === 'failed').length;

  return (
    <div className="space-y-6">
      {jobsError && <DataLoadError what="scheduler jobs" />}
      {/* Header */}
      <div>
        <h2 className="text-2xl font-bold text-slate-900">Scheduler</h2>
        <p className="text-slate-500 text-sm mt-1">Manage scheduled pipeline jobs and cron configurations.</p>
      </div>

      {/* Stat Cards */}
      <div className="grid grid-cols-4 gap-4">
        <StatCard label="Total Jobs" value={totalJobs} icon={Repeat} iconColor="text-blue-600" />
        <StatCard label="Active" value={active} icon={PlayCircle} iconColor="text-green-600" />
        <StatCard label="Paused" value={paused} icon={PauseCircle} iconColor="text-slate-500" />
        <StatCard label="Failed 24h" value={failedLast24h} icon={XCircle} iconColor="text-rose-500" />
      </div>

      {/* Jobs Table */}
      <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
        <table className="w-full">
          <thead>
            <tr className="text-xs text-slate-400 font-medium border-b border-slate-100 bg-slate-50/50">
              <th className="text-left px-6 py-3">Job Name</th>
              <th className="text-left px-6 py-3">Schedule</th>
              <th className="text-left px-6 py-3">Last Run</th>
              <th className="text-left px-6 py-3">Last Status</th>
              <th className="text-left px-6 py-3">Next Run</th>
              <th className="text-center px-6 py-3">Actions</th>
            </tr>
          </thead>
          <tbody>
            {jobs.map((job) => (
              <tr
                key={job.name}
                className="border-b border-slate-50 last:border-0 hover:bg-slate-50/50 transition group"
              >
                <td className="px-6 py-4">
                  <div>
                    <p className="text-sm font-semibold text-slate-900">{job.name}</p>
                    <p className="text-xs text-slate-400 mt-0.5">{job.project}</p>
                  </div>
                </td>
                <td className="px-6 py-4">
                  <code className="px-2 py-1 text-xs font-mono font-bold text-primary bg-primary/5 rounded">
                    {job.schedule}
                  </code>
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">{job.lastRun}</td>
                <td className="px-6 py-4">
                  <StatusBadge variant={job.lastStatus} />
                </td>
                <td className="px-6 py-4 text-sm text-slate-500">{job.nextRun}</td>
                <td className="px-6 py-4">
                  <div className="flex items-center justify-center gap-3">
                    <button
                      className="text-slate-400 hover:text-primary transition"
                      title="Run now"
                    >
                      <Play size={20} weight="regular" />
                    </button>
                    <label className="relative inline-flex items-center cursor-pointer">
                      <input
                        type="checkbox"
                        checked={job.enabled}
                        readOnly
                        className="sr-only peer"
                      />
                      <div className={cn(
                        'w-9 h-5 rounded-full transition-colors',
                        job.enabled ? 'bg-primary' : 'bg-slate-300',
                      )}>
                        <div className={cn(
                          'w-4 h-4 mt-0.5 rounded-full bg-white shadow transition-transform',
                          job.enabled ? 'translate-x-4.5 ml-[18px]' : 'translate-x-0.5 ml-[2px]',
                        )} />
                      </div>
                    </label>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {/* Pagination Footer */}
        <div className="flex items-center justify-between px-6 py-3 border-t border-slate-100 bg-slate-50/30">
          <p className="text-xs text-slate-400">
            Showing {jobs.length} of {totalJobs} jobs
          </p>
          <div className="flex items-center gap-1">
            <button className="px-3 py-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition" disabled>
              Previous
            </button>
            <button className="px-2.5 py-1 text-xs font-bold text-white bg-primary rounded-md">1</button>
            <button className="px-3 py-1 text-xs font-medium text-slate-400 hover:text-slate-600 transition" disabled>
              Next
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export const Route = createFileRoute('/_authenticated/pipeline/scheduler')({
  component: SchedulerPage,
});
